/**
 * NotebookErrorsDrawer (PHASE 7)
 * ==============================
 * Aggregates notebook + file-level errors and displays them in a single drawer.
 *
 * Contract:
 * - Caller passes pre-normalized UIUniversalError objects.
 * - Items should be deduped upstream by fileId/code/message.
 */

import React from 'react';

import { X, AlertTriangle, ChevronRight, FileText } from 'lucide-react';

export default function NotebookErrorsDrawer({ open, onClose, items = [], onSelect }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <button className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close errors" />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-neutral-900 border-l border-slate-200 dark:border-neutral-700 shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-neutral-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-semibold text-slate-800 dark:text-neutral-100 truncate">
                Errors ({items.length})
              </h3>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-neutral-400">
              These errors may affect ingestion, search, or chat accuracy.
            </p>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {items.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 dark:text-neutral-400">No errors.</div>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => {
                const name = it.fileName || 'Notebook';
                const code = it.error?.code || 'UNKNOWN_ERROR';
                const msg = it.error?.message || 'An unexpected error occurred';
                const ts = it.error?.timestamp ? new Date(it.error.timestamp).toLocaleString() : '';
                return (
                  <button
                    key={`${it.scope || 'file'}:${it.fileId || 'nb'}:${code}:${idx}`}
                    className="w-full text-left p-3 rounded-xl border border-slate-200 dark:border-neutral-700 hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
                    onClick={() => onSelect?.(it)}
                    data-testid="istudio-errors-drawer-item"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-neutral-800">
                            <FileText className="h-3.5 w-3.5 text-slate-600 dark:text-neutral-300" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-800 dark:text-neutral-100 truncate">
                              {name}
                            </div>
                            <div className="mt-0.5 text-[11px] text-slate-500 dark:text-neutral-400 truncate">
                              {it.scope === 'file' ? `fileId: ${it.fileId || 'unknown'}` : 'notebook-level'}
                              {it.status ? ` · status: ${it.status}` : ''}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-[11px] font-mono text-red-700 dark:text-red-300">
                          {code}
                        </div>
                        <div className="mt-1 text-xs text-slate-700 dark:text-neutral-200 whitespace-pre-wrap break-words">
                          {msg}
                        </div>
                        {ts ? (
                          <div className="mt-1 text-[10px] text-slate-400 dark:text-neutral-500">
                            {ts}
                          </div>
                        ) : null}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

